import { useEffect, useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  Input,
  Select,
  LoadingSkeleton,
  Badge,
  Modal,
} from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../../shared/utils/format';
import type { ContractItem, ContractPeriodicity } from './types';
import { useContract, useUpdateContract } from './useContractQueries';

/* ── Detalhes e Edição de Contrato (R2C, tela "erpone_detalhes_e_edi_o_de_contrato")
 * Consome GET /contracts/:id (detalhe) e PATCH /contracts/:id (edição).
 * Protótipo: Software Licensing Agreement — Item Code, Description, SLA
 * Target, Monthly Cost; Vendor/Financial Terms; edição dos itens.
 */

const PERIODICITY_LABEL: Record<ContractPeriodicity, string> = {
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quinzenal',
  MONTHLY: 'Mensal',
  BIMONTHLY: 'Bimestral',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
};

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  CANCELLED: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Ativo',
  SUSPENDED: 'Suspenso',
  CANCELLED: 'Cancelado',
};

interface EditableItem {
  key: string;
  productId: string;
  description: string;
  quantity: string;
  unitValue: string;
  slaTarget: string;
  monthlyCost: string;
}

function contractTitle(contract: {
  id: string;
  items: ContractItem[];
}): string {
  return contract.items[0]?.description || `Contrato ${contract.id}`;
}

function toEditable(item: ContractItem, index: number): EditableItem {
  return {
    key: item.id || `new-${index}`,
    productId: item.productId,
    description: item.description,
    quantity: String(item.quantity),
    unitValue: String(item.unitValue),
    slaTarget: item.slaTarget ?? '',
    monthlyCost: item.monthlyCost !== null && item.monthlyCost !== undefined ? String(item.monthlyCost) : '',
  };
}

export function ContractDetailPage(): JSX.Element {
  const params = useParams({ strict: false }) as { contractId?: string };
  const contractId = params.contractId ?? '';

  const { data: contract, isLoading, error } = useContract(contractId);
  const updateContract = useUpdateContract(contractId);

  const [editing, setEditing] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [periodicity, setPeriodicity] = useState<ContractPeriodicity>('MONTHLY');
  const [value, setValue] = useState('');
  const [items, setItems] = useState<EditableItem[]>([]);
  const [formError, setFormError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);

  /* Inicializa o formulário de edição quando o contrato carrega. */
  useEffect(() => {
    if (!contract) return;
    setStartDate(contract.startDate.slice(0, 10));
    setEndDate(contract.endDate ? contract.endDate.slice(0, 10) : '');
    setPeriodicity(contract.periodicity);
    setValue(String(contract.value));
    setItems(contract.items.map(toEditable));
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract?.id]);

  const totals = useMemo(() => {
    if (!contract) return { monthly: 0, annual: 0 };
    const monthly = contract.items.reduce(
      (sum, item) => sum + (item.monthlyCost ?? item.unitValue * item.quantity),
      0,
    );
    return { monthly, annual: monthly * 12 };
  }, [contract]);

  const num = (s: string): number | null => {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const updateItem = (key: string, field: keyof EditableItem, val: string): void => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: val } : it)));
  };

  const addItem = (): void => {
    setItems((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        productId: '',
        description: '',
        quantity: '1',
        unitValue: '0',
        slaTarget: '',
        monthlyCost: '',
      },
    ]);
  };

  const removeItem = (key: string): void => {
    setItems((prev) => prev.filter((it) => it.key !== key));
  };

  const valid = (): boolean => {
    if (!startDate) {
      setFormError('Informe a data de início.');
      return false;
    }
    const v = num(value);
    if (v === null || v < 0) {
      setFormError('Valor mensal deve ser um número ≥ 0.');
      return false;
    }
    if (items.length === 0) {
      setFormError('Adicione ao menos um item ao contrato.');
      return false;
    }
    for (const it of items) {
      if (!it.description.trim() || !it.productId.trim()) {
        setFormError('Todo item precisa de código (Item Code) e descrição.');
        return false;
      }
    }
    setFormError('');
    return true;
  };

  const submit = (): void => {
    if (!valid()) return;
    const v = num(value);
    const payload = {
      startDate,
      ...(endDate ? { endDate } : { endDate: null }),
      periodicity,
      ...(v !== null ? { value: v } : {}),
      items: items.map((it) => {
        const qty = num(it.quantity);
        const uv = num(it.unitValue);
        const mc = num(it.monthlyCost);
        const existing = contract?.items.find((c) => c.id === (it.key.startsWith('new-') ? '' : it.key));
        return {
          ...(existing ? { id: existing.id } : {}),
          productId: it.productId.trim(),
          description: it.description.trim(),
          ...(qty !== null ? { quantity: qty } : {}),
          ...(uv !== null ? { unitValue: uv } : {}),
          ...(it.slaTarget.trim() ? { slaTarget: it.slaTarget.trim() } : { slaTarget: null }),
          ...(mc !== null ? { monthlyCost: mc } : { monthlyCost: null }),
        };
      }),
    };
    updateContract.mutate(payload, {
      onSuccess: () => {
        setSavedMsg('Contrato atualizado.');
        setEditing(false);
        window.setTimeout(() => setSavedMsg(''), 4000);
      },
      onError: (err) => setFormError((err as Error).message),
    });
  };

  const startEdit = (): void => {
    if (!contract) return;
    setEditing(true);
    setSavedMsg('');
  };

  const cancelEdit = (): void => {
    if (!contract) return;
    setEditing(false);
    setStartDate(contract.startDate.slice(0, 10));
    setEndDate(contract.endDate ? contract.endDate.slice(0, 10) : '');
    setPeriodicity(contract.periodicity);
    setValue(String(contract.value));
    setItems(contract.items.map(toEditable));
    setFormError('');
  };

  if (!contractId) {
    return (
      <div className="p-xl">
        <Card>
          <p className="mb-xs mt-0 font-headline-sm text-headline-sm text-on-surface">
            Nenhum contrato selecionado
          </p>
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            Selecione um contrato na lista para ver os detalhes e editar.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-xl flex flex-col gap-lg">
      {isLoading ? (
        <LoadingSkeleton count={4} height="72px" />
      ) : error || !contract ? (
        <Card>
          <p className="m-0 font-body-md text-body-md text-error">
            {(error as Error)?.message ?? 'Contrato não encontrado.'}
          </p>
        </Card>
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-md">
            <div>
              <p className="mb-xs font-label-sm text-label-sm uppercase tracking-wide text-on-surface-variant">
                Contrato {contract.id}
              </p>
              <h1 className="mb-xs mt-0 font-display-lg text-display-lg text-on-surface">
                {contractTitle(contract)}
              </h1>
              <div className="flex flex-wrap items-center gap-sm">
                <Badge variant={STATUS_VARIANT[contract.status] ?? 'neutral'}>
                  {STATUS_LABEL[contract.status] ?? contract.status}
                </Badge>
                <span className="font-label-md text-label-md text-on-surface-variant">
                  {PERIODICITY_LABEL[contract.periodicity]} · início{' '}
                  {formatDate(contract.startDate)}
                </span>
              </div>
            </div>
            {!editing ? (
              <div className="flex gap-sm">
                <Button variant="outline" onClick={startEdit}>
                  Editar contrato
                </Button>
              </div>
            ) : null}
          </div>

          {savedMsg ? (
            <p className="mb-0 font-body-md text-body-md text-success">{savedMsg}</p>
          ) : null}

          {/* Resumo financeiro */}
          <div className="grid grid-cols-1 gap-md sm:grid-cols-3">
            <Card>
              <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
                Valor mensal
              </p>
              <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
                {formatCurrency(contract.value)}
              </p>
            </Card>
            <Card>
              <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
                TCV anual estimado
              </p>
              <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
                {formatCurrency(totals.annual)}
              </p>
            </Card>
            <Card>
              <p className="mb-xs mt-0 font-label-md text-label-md uppercase text-on-surface-variant">
                Próxima cobrança
              </p>
              <p className="m-0 font-display-lg text-display-lg tabular-nums text-on-surface">
                {formatDate(contract.nextBillingDate)}
              </p>
            </Card>
          </div>

          {/* Datas */}
          <Card>
            <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
              Termos do contrato
            </h3>
            <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="mb-xs mt-0 font-label-sm text-label-sm text-on-surface-variant">
                  Início
                </p>
                <p className="m-0 font-body-md text-body-md text-on-surface">
                  {formatDate(contract.startDate)}
                </p>
              </div>
              <div>
                <p className="mb-xs mt-0 font-label-sm text-label-sm text-on-surface-variant">
                  Término
                </p>
                <p className="m-0 font-body-md text-body-md text-on-surface">
                  {formatDate(contract.endDate)}
                </p>
              </div>
              <div>
                <p className="mb-xs mt-0 font-label-sm text-label-sm text-on-surface-variant">
                  Última cobrança
                </p>
                <p className="m-0 font-body-md text-body-md text-on-surface">
                  {formatDate(contract.lastBillingDate)}
                </p>
              </div>
              <div>
                <p className="mb-xs mt-0 font-label-sm text-label-sm text-on-surface-variant">
                  Periodicidade
                </p>
                <p className="m-0 font-body-md text-body-md text-on-surface">
                  {PERIODICITY_LABEL[contract.periodicity]}
                </p>
              </div>
            </div>
            {contract.cancellationReason ? (
              <p className="mb-0 mt-sm font-body-sm text-body-sm text-error">
                Cancelamento: {contract.cancellationReason}
              </p>
            ) : null}
          </Card>

          {/* Itens / SLA */}
          {!editing ? (
            <Card padding="0">
              <div className="border-b border-outline-variant p-md">
                <h3 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
                  Itens do serviço / SLA
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left font-body-sm">
                  <thead className="bg-surface font-label-sm text-on-surface-variant">
                    <tr>
                      <th className="p-md font-medium">Item Code</th>
                      <th className="p-md font-medium">Descrição</th>
                      <th className="p-md font-medium">SLA Target</th>
                      <th className="p-md font-medium text-right">Qtde</th>
                      <th className="p-md font-medium text-right">Valor unit.</th>
                      <th className="p-md font-medium text-right">Custo mensal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contract.items.map((item) => (
                      <tr
                        key={item.id}
                        className="border-t border-outline-variant/60 hover:bg-surface-container-highest"
                      >
                        <td className="p-md font-label-md text-primary">{item.productId}</td>
                        <td className="p-md">{item.description}</td>
                        <td className="p-md text-on-surface-variant">
                          {item.slaTarget ?? '—'}
                        </td>
                        <td className="p-md text-right tabular-nums">{item.quantity}</td>
                        <td className="p-md text-right tabular-nums">
                          {formatCurrency(item.unitValue)}
                        </td>
                        <td className="p-md text-right font-label-md tabular-nums text-on-surface">
                          {formatCurrency(item.monthlyCost ?? item.unitValue * item.quantity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            /* ── Modo edição ── */
            <Card>
              <h3 className="mb-sm mt-0 font-headline-sm text-headline-sm text-on-surface">
                Editar contrato
              </h3>
              <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-4">
                <Input
                  id="contract-start"
                  label="Início *"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  id="contract-end"
                  label="Término"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <div>
                  <div className="field-label mb-xs">Periodicidade</div>
                  <Select
                    aria-label="Periodicidade"
                    value={periodicity}
                    onChange={(e) => setPeriodicity(e.target.value as ContractPeriodicity)}
                  >
                    {(Object.keys(PERIODICITY_LABEL) as ContractPeriodicity[]).map((p) => (
                      <option key={p} value={p}>
                        {PERIODICITY_LABEL[p]}
                      </option>
                    ))}
                  </Select>
                </div>
                <Input
                  id="contract-value"
                  label="Valor mensal (R$) *"
                  type="number"
                  min="0"
                  step="0.01"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>

              <div className="mt-md">
                <div className="mb-sm flex items-center justify-between">
                  <h4 className="mb-0 mt-0 font-headline-sm text-headline-sm text-on-surface">
                    Itens
                  </h4>
                  <Button variant="outline" size="sm" onClick={addItem}>
                    + Adicionar item
                  </Button>
                </div>
                <div className="flex flex-col gap-md">
                  {items.map((it, idx) => (
                    <div
                      key={it.key}
                      className="rounded-lg border border-outline-variant p-sm"
                    >
                      <div className="mb-sm flex items-center justify-between">
                        <span className="font-label-md text-label-md text-on-surface-variant">
                          Item {idx + 1}
                        </span>
                        <button
                          type="button"
                          className="font-label-md text-label-md text-error hover:underline"
                          onClick={() => removeItem(it.key)}
                        >
                          Remover
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-md sm:grid-cols-2 lg:grid-cols-3">
                        <Input
                          id={`item-code-${idx}`}
                          label="Item Code *"
                          value={it.productId}
                          onChange={(e) => updateItem(it.key, 'productId', e.target.value)}
                          placeholder="LIC-DB-EE"
                        />
                        <Input
                          id={`item-desc-${idx}`}
                          label="Descrição *"
                          value={it.description}
                          onChange={(e) => updateItem(it.key, 'description', e.target.value)}
                          placeholder="Database Enterprise Edition"
                        />
                        <Input
                          id={`item-sla-${idx}`}
                          label="SLA Target"
                          value={it.slaTarget}
                          onChange={(e) => updateItem(it.key, 'slaTarget', e.target.value)}
                          placeholder="99.99% Uptime"
                        />
                        <Input
                          id={`item-qty-${idx}`}
                          label="Qtde"
                          type="number"
                          min="0"
                          step="1"
                          value={it.quantity}
                          onChange={(e) => updateItem(it.key, 'quantity', e.target.value)}
                        />
                        <Input
                          id={`item-unit-${idx}`}
                          label="Valor unitário (R$)"
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.unitValue}
                          onChange={(e) => updateItem(it.key, 'unitValue', e.target.value)}
                        />
                        <Input
                          id={`item-monthly-${idx}`}
                          label="Custo mensal (R$)"
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.monthlyCost}
                          onChange={(e) => updateItem(it.key, 'monthlyCost', e.target.value)}
                          placeholder="Ex.: 25000"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {formError ? (
                <p className="mt-sm font-body-sm text-body-sm text-error">{formError}</p>
              ) : null}

              <div className="mt-md flex flex-wrap gap-sm">
                <Button
                  variant="primary"
                  loading={updateContract.isPending}
                  onClick={submit}
                >
                  Salvar alterações
                </Button>
                <Button variant="outline" onClick={() => setShowConfirmDiscard(true)}>
                  Cancelar
                </Button>
              </div>
            </Card>
          )}
        </>
      )}

      <Modal
        isOpen={showConfirmDiscard}
        onClose={() => setShowConfirmDiscard(false)}
        title="Descartar alterações?"
        footer={
          <div className="flex gap-sm">
            <Button variant="outline" onClick={() => setShowConfirmDiscard(false)}>
              Continuar editando
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setShowConfirmDiscard(false);
                cancelEdit();
              }}
            >
              Descartar
            </Button>
          </div>
        }
      >
        <p className="m-0 font-body-md text-body-md text-on-surface-variant">
          As alterações não salvas neste contrato serão perdidas.
        </p>
      </Modal>
    </div>
  );
}